import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { customFields, coins, labelTemplates } from '../../utils/api';
import { toast } from 'react-toastify';
import PreviewModal from './PreviewModal';

const FILTER_FIELDS = [
  { id: 'assayer', label: 'Assayer', type: 'text' },
  { id: 'cac_sticker', label: 'CAC Sticker', type: 'select', options: ['green', 'gold', 'none'] },
  { id: 'certification_number', label: 'Certification Number', type: 'text' },
  { id: 'certification_service_id', label: 'Certification Service', type: 'text' },
  { id: 'coin_details', label: 'Coin Details', type: 'text' },
  { id: 'composition', label: 'Composition', type: 'text' },
  { id: 'country', label: 'Country', type: 'text' },
  { id: 'current_pop_higher', label: 'Current Pop Higher', type: 'number' },
  { id: 'current_population', label: 'Current Population', type: 'number' },
  { id: 'denomination', label: 'Denomination', type: 'text' },
  { id: 'diameter', label: 'Diameter', type: 'number' },
  { id: 'die_variety', label: 'Die Variety', type: 'text' },
  { id: 'edge', label: 'Edge', type: 'text' },
  { id: 'grade', label: 'Grade', type: 'text' },
  { id: 'grading_cost', label: 'Grading Cost', type: 'number' },
  { id: 'list_price', label: 'List Price', type: 'number' },
  { id: 'metal', label: 'Metal', type: 'text' },
  { id: 'minor_variety', label: 'Minor Variety', type: 'text' },
  { id: 'mint_mark', label: 'Mint Mark', type: 'text' },
  { id: 'mintage', label: 'Mintage', type: 'text' },
  { id: 'notes', label: 'Notes', type: 'text' },
  { id: 'pcgs_number', label: 'PCGS Number', type: 'text' },
  { id: 'pop_higher_when_bought', label: 'Pop Higher When Bought', type: 'number' },
  { id: 'population_when_bought', label: 'Population When Bought', type: 'number' },
  { id: 'price_guide_value', label: 'Price Guide Value', type: 'number' },
  { id: 'provenance', label: 'Provenance', type: 'text' },
  { id: 'purchase_date', label: 'Purchase Date', type: 'date' },
  { id: 'purchase_price', label: 'Purchase Price', type: 'number' },
  { id: 'reference_number', label: 'Reference Number', type: 'text' },
  { id: 'shipping_cost', label: 'Shipping Cost', type: 'number' },
  { id: 'status', label: 'Status', type: 'text' },
  { id: 'storage_location', label: 'Storage Location', type: 'text' },
  { id: 'total_cost', label: 'Total Cost', type: 'number' },
  { id: 'variety', label: 'Variety', type: 'text' },
  { id: 'weight', label: 'Weight', type: 'number' },
  { id: 'year', label: 'Year', type: 'text' }
];

const TemplateList = ({ templates, onEdit, onDelete }) => {
  const [availableCustomFields, setAvailableCustomFields] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewCoin, setPreviewCoin] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const fields = await customFields.getAll();
        setAvailableCustomFields(fields);

        const coinsResponse = await coins.getAll({ limit: 1 });
        if (coinsResponse?.length > 0) {
          setPreviewCoin(coinsResponse[0]);
        }
      } catch (err) {
        toast.error('Failed to load data');
      }
    };

    loadData();
  }, []);

  const getTypeLabel = (type) => {
    switch (type) {
      case 'qr_code':
        return 'QR Code';
      case 'barcode':
        return 'Barcode';
      case 'text_label':
        return 'Text Label';
      default:
        return type;
    }
  };

  const formatDimensions = (width, height) => {
    return `${width} × ${height} mm`;
  };

  if (!templates?.length) {
    return (
      <div className="text-center py-6 text-gray-500">
        No templates found. Create one to get started.
      </div>
    );
  }

  const renderTable = () => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Dimensions
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Selected Fields
            </th>
            <th scope="col" className="relative px-6 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {templates.map((template) => (
            <tr 
              key={template.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => {
                setSelectedTemplate(template);
                setIsPreviewOpen(true);
              }}
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {template.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {getTypeLabel(template.type)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDimensions(template.width_mm, template.height_mm)}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {template.description || '-'}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500">
                <div className="flex flex-wrap gap-1">
                  {(template.coin_fields || []).map(field => {
                    const fieldId = field.id || field;
                    if (fieldId.startsWith('custom_field_')) {
                      const customField = availableCustomFields.find(
                        f => `custom_field_${f.id}` === fieldId
                      );
                      return customField ? (
                        <span key={fieldId} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {customField.name}
                        </span>
                      ) : null;
                    }
                    const fieldDef = FILTER_FIELDS.find(f => f.id === fieldId);
                    return fieldDef ? (
                      <span key={fieldId} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {fieldDef.label}
                      </span>
                    ) : null;
                  })}
                  {(!template.coin_fields?.length) && '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(template);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <PencilIcon className="h-5 w-5" />
                    <span className="sr-only">Edit</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(template);
                    }}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-5 w-5" />
                    <span className="sr-only">Delete</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="overflow-hidden">
      {renderTable()}
      {isPreviewOpen && selectedTemplate && previewCoin && (
        <PreviewModal
          template={selectedTemplate}
          coin={previewCoin}
          customFields={availableCustomFields}
          filterFields={FILTER_FIELDS}
          onClose={() => setIsPreviewOpen(false)}
          onSave={async (fieldStyles) => {
            try {
              const updatedTemplate = {
                ...selectedTemplate,
                coin_fields: fieldStyles
              };
              await labelTemplates.update(selectedTemplate.id, updatedTemplate);
              toast.success('Template styles updated successfully');
              setIsPreviewOpen(false);
              window.location.reload();
            } catch (err) {
              toast.error('Failed to update template styles');
            }
          }}
        />
      )}
    </div>
  );
};

export default TemplateList;
